// frontend/src/components/Orders.js

import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Chip,
    Paper,
    Stack,
    useMediaQuery,
    Divider,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    IconButton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    GridToolbarExport,
} from '@mui/x-data-grid';
import {
    ErrorOutlineOutlined as ErrorOutline,
    LocalShipping,
    SearchRounded,
    RestartAltRounded,
    ViewCompactRounded,
    TableRowsRounded,
    CancelOutlined,
} from '@mui/icons-material';
import { getClientsOrders, getClientsName, getIssueStatuses } from '../services/api';
import Layout from './Layout';
import moment from 'moment';
import IssueDialog from './IssueDialog';
import DispatchDialog from './DispatchDialog'; // Import the DispatchDialog
import CancelOrderDialog from './CancelOrderDialog';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useTheme, alpha } from '@mui/material/styles';

const categoryColors = {
    'Giant Cards': '#dc004e',
    'Wrapping Paper': '#ff9800',
    GiftOnly: '#20c997',
    Personlaised: '#7c4dff',
    Personalised: '#7c4dff',
    'CARD ONLY': '#dc004e',
    'NO CARD': '#ff9800',
    'CARD WITH BOTTLE': '#20c997',
};

const getCategoryChipStyles = (categoryName) => ({
    backgroundColor: categoryColors[categoryName] || '#5c6bc0',
    color: '#fff',
    fontWeight: 600,
    minWidth: 88,
});

const formatDateTime = (value) => {
    if (!value) return 'N/A';

    const raw = String(value).trim();
    const localMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (localMatch) {
        const [, year, month, day, hour, minute] = localMatch;
        return `${year}-${month}-${day} ${hour}:${minute}`;
    }

    return moment(value).format('YYYY-MM-DD HH:mm');
};

const getDefaultServerFilters = () => ({
    client: 'ALL',
    status: 'ALL',
    dateFrom: moment().format('YYYY-MM-DD'),
    dateTo: moment().format('YYYY-MM-DD'),
    useCutoff: false,
    issueStatus: 'ALL',
});

const normalizeSearchValue = (value) => String(value || '').trim().toLowerCase();

const hasActiveIssue = (order) => Boolean(order.issueStatus && order.issueStatus !== 'Resolved');

const getQueueStateMeta = (order) => {
    if (order.orderStatus === 'Cancelled') {
        return { label: 'Cancelled', tone: 'default' };
    }

    if (order.orderStatus === 'Dispatched') {
        return { label: 'Dispatched', tone: 'success' };
    }

    if (hasActiveIssue(order)) {
        return { label: 'Issue Active', tone: 'warning' };
    }

    if (order.orderStatus === 'New') {
        return { label: 'Ready', tone: 'info' };
    }

    return { label: 'Review', tone: 'default' };
};

const matchesQuickSearch = (order, query) => {
    if (!query) return true;

    const searchableText = [
        order.orderNumber,
        order.clientName,
        order.category,
        order.orderStatus,
        order.issueStatus,
        order.fulfilmentChannel,
        order.shippingCarrier,
        order.shippingMethod,
        order.tracking,
        order.dispatchedByUser,
        order.comments,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    return searchableText.includes(query);
};

const getResponsiveColumnVisibilityModel = (isTablet, isMobile, viewPreset) => {
    if (isMobile) {
        return {
            actions: true,
            orderNumber: true,
            clientName: true,
            queueState: true,
            orderStatus: true,
            issueStatus: true,
            category: false,
            createdAt: false,
            dispatchedDate: false,
            dispatchedByUser: false,
            fulfilmentChannel: false,
            shippingCarrier: false,
            shippingMethod: false,
            tracking: false,
        };
    }

    if (isTablet) {
        return {
            actions: true,
            orderNumber: true,
            clientName: true,
            queueState: true,
            category: true,
            orderStatus: true,
            issueStatus: true,
            createdAt: true,
            dispatchedDate: false,
            dispatchedByUser: false,
            fulfilmentChannel: true,
            shippingCarrier: true,
            shippingMethod: false,
            tracking: true,
        };
    }

    if (viewPreset === 'full') {
        return {
            actions: true,
            orderNumber: true,
            clientName: true,
            queueState: true,
            category: true,
            orderStatus: true,
            issueStatus: true,
            createdAt: true,
            dispatchedDate: true,
            dispatchedByUser: true,
            fulfilmentChannel: true,
            shippingCarrier: true,
            shippingMethod: true,
            tracking: true,
        };
    }

    return {
        actions: true,
        orderNumber: true,
        clientName: true,
        queueState: true,
        category: true,
        orderStatus: true,
        issueStatus: true,
        createdAt: true,
        dispatchedDate: false,
        dispatchedByUser: false,
        fulfilmentChannel: true,
        shippingCarrier: true,
        shippingMethod: true,
        tracking: false,
    };
};

const OrdersGridToolbar = () => (
    <GridToolbarContainer
        sx={{
            px: 0.5,
            py: 0.75,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
        }}
    >
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
            <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
        </Stack>
    </GridToolbarContainer>
);

const Orders = () => {
    const defaultServerFilters = useMemo(() => getDefaultServerFilters(), []);
    const [orders, setOrders] = useState([]);
    const [clients, setClients] = useState([]);
    const [client, setClient] = useState(defaultServerFilters.client);
    const [status, setStatus] = useState(defaultServerFilters.status);
    const [dateFrom, setDateFrom] = useState(defaultServerFilters.dateFrom);
    const [dateTo, setDateTo] = useState(defaultServerFilters.dateTo);
    const [useCutoff, setUseCutoff] = useState(defaultServerFilters.useCutoff);
    const [openIssueDialog, setOpenIssueDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [issueStatus, setIssueStatus] = useState(defaultServerFilters.issueStatus);
    const [issueStatuses, setIssueStatuses] = useState([]);
    const [openDispatchDialog, setOpenDispatchDialog] = useState(false);
    const [dispatchOrder, setDispatchOrder] = useState(null);
    const [openCancelDialog, setOpenCancelDialog] = useState(false);
    const [cancelOrderObj, setCancelOrderObj] = useState(null);
    const [quickSearch, setQuickSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('ALL');
    const [channelFilter, setChannelFilter] = useState('ALL');
    const [viewPreset, setViewPreset] = useState('operations');
    const { showSnackbar } = useSnackbar();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const responsiveColumnVisibilityModel = useMemo(
        () => getResponsiveColumnVisibilityModel(isTablet, isMobile, viewPreset),
        [isTablet, isMobile, viewPreset]
    );
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(responsiveColumnVisibilityModel);

    useEffect(() => {
        setColumnVisibilityModel(responsiveColumnVisibilityModel);
    }, [responsiveColumnVisibilityModel]);
    
    // Fetch clients on component mount
    useEffect(() => {
        const fetchClients = async () => {
            try {
                const response = await getClientsName();
                if (response.success) {
                    setClients(response.clients);
                } else {
                    console.error('Failed to fetch clients:', response.message);
                }
            } catch (error) {
                console.error('Error fetching clients:', error);
            }
        };
        fetchClients();
    }, []);

    // Fetch issue statuses on component mount
    useEffect(() => {
        const fetchIssueStatuses = async () => {
            try {
                const response = await getIssueStatuses();
                if (response.success) {
                    setIssueStatuses(response.statuses);
                } else {
                    console.error('Failed to fetch issue statuses:', response.message);
                }
            } catch (error) {
                console.error('Error fetching issue statuses:', error);
            }
        };
        fetchIssueStatuses();
    }, []);

    // Load orders based on filters
    const loadOrders = async (overrides = {}) => {
        const filters = {
            client,
            status,
            dateFrom,
            dateTo,
            useCutoff,
            issueStatus,
            ...overrides,
        };
        try {
            const response = await getClientsOrders(filters);
            if (response.success) {
                setOrders(response.orders);
            } else {
                console.error('Failed to fetch orders:', response.message);
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
        }
    };

    // Load orders on initial render
    useEffect(() => {
        loadOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);  // Empty dependency array to run once on mount

    const handleSearch = () => {
        loadOrders();
    };

    const handleResetFilters = () => {
        setClient(defaultServerFilters.client);
        setStatus(defaultServerFilters.status);
        setDateFrom(defaultServerFilters.dateFrom);
        setDateTo(defaultServerFilters.dateTo);
        setUseCutoff(defaultServerFilters.useCutoff);
        setIssueStatus(defaultServerFilters.issueStatus);
        setQuickSearch('');
        setCategoryFilter('ALL');
        setChannelFilter('ALL');
        setViewPreset('operations');
        loadOrders(defaultServerFilters);
    };

    const handleApplyDatePreset = (daysBack) => {
        const nextDateTo = moment().format('YYYY-MM-DD');
        const nextDateFrom = moment().subtract(daysBack, 'days').format('YYYY-MM-DD');

        setUseCutoff(false);
        setDateFrom(nextDateFrom);
        setDateTo(nextDateTo);
        loadOrders({
            dateFrom: nextDateFrom,
            dateTo: nextDateTo,
            useCutoff: false,
        });
    };

    // Validation to ensure dateFrom is not greater than dateTo
    const handleDateFromChange = (e) => {
        const selectedFromDate = e.target.value;
        if (moment(selectedFromDate).isAfter(dateTo)) {
            showSnackbar('From Date cannot be later than To Date.', 'warning');
            return; // Do not update the state
        }
        setDateFrom(selectedFromDate);
    };

    const handleDateToChange = (e) => {
        const selectedToDate = e.target.value;
        if (moment(selectedToDate).isBefore(dateFrom)) {
            showSnackbar('To Date cannot be earlier than From Date.', 'warning');
            return; // Do not update the state
        }
        setDateTo(selectedToDate);
    };

    // Disable status and date range when "Required Dispatch Today Orders" is checked
    const handleCutoffChange = (e) => {
        setUseCutoff(e.target.checked);
        if (e.target.checked) {
            setStatus('ALL'); // Reset status to 'ALL' when the checkbox is checked
            setIssueStatus('ALL'); // Reset issueStatus to 'ALL' as well
        }
    };

    const handleAddIssueClick = (order) => {
        setSelectedOrder(order);
        setOpenIssueDialog(true);
    };

    const handleIssueDialogClose = () => {
        setOpenIssueDialog(false);
        setSelectedOrder(null);
    };

    const handleIssueAdded = () => {
        // Refresh the orders list or update the order in state
        loadOrders();
    };

    const handleDispatchClick = (order) => {
        setDispatchOrder(order);
        setOpenDispatchDialog(true);
    };

    const handleDispatchDialogClose = () => {
        setOpenDispatchDialog(false);
        setDispatchOrder(null);
    };

    const handleOrderDispatched = () => {
        // Refresh the orders list or update the specific order
        loadOrders();
    };

    const handleCancelClick = (order) => {
        setCancelOrderObj(order);
        setOpenCancelDialog(true);
    };
    const handleCancelDialogClose = () => {
        setOpenCancelDialog(false);
        setCancelOrderObj(null);
    };
    const handleOrderCancelled = () => {
        showSnackbar('Order cancelled successfully.', 'success');
        loadOrders();
    };

    const handleCancelError = (err) => {
        const msg =
            err?.response?.data?.message ||
            err?.message ||
            'Error cancelling order.';
        showSnackbar(msg, 'error');
    };

    const getChipStyles = (status) => {
        switch (status) {
            case 'Error':
                return {
                    backgroundColor: '#dc3545', // Red
                    color: '#fff',
                };
            case 'Resolved':
                return {
                    backgroundColor: '#4caf50', // Green
                    color: '#fff',
                };
            case 'In Progress':
                return {
                    backgroundColor: '#ff9800', // Orange
                    color: '#fff',
                };
            case 'On Hold':
                return {
                    backgroundColor: '#9c27b0', // Purple
                    color: '#fff',
                };
            case 'Urgent':
                return {
                    backgroundColor: '#F27360', // Custom color
                    color: '#fff',
                };
            default:
                return {
                    backgroundColor: '#e0e0e0', // Grey for unknown statuses
                    color: '#000',
                };
        }
    };

    const getOrderStatusChipStyles = (status) => {
        switch (status) {
            case 'New':
                return {
                    backgroundColor: '#2196f3', // Blue
                    color: '#fff',
                };
            case 'Dispatched':
                return {
                    backgroundColor: '#4caf50', // Green
                    color: '#fff',
                };
            case 'Cancelled':
                return {
                    backgroundColor: '#dc3545', // Red
                    color: '#fff',
                };
            default:
                return {
                    backgroundColor: '#9e9e9e', // Grey for unknown statuses
                    color: '#fff',
                };
        }
    };

    const availableCategories = useMemo(
        () => [...new Set(orders.map((order) => order.category).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
        [orders]
    );

    const availableChannels = useMemo(
        () => [...new Set(orders.map((order) => order.fulfilmentChannel).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
        [orders]
    );

    const displayedOrders = useMemo(() => {
        const normalizedQuery = normalizeSearchValue(quickSearch);

        return orders
            .filter((order) => {
                if (categoryFilter !== 'ALL' && order.category !== categoryFilter) {
                    return false;
                }

                if (channelFilter !== 'ALL' && order.fulfilmentChannel !== channelFilter) {
                    return false;
                }

                return matchesQuickSearch(order, normalizedQuery);
            })
            .map((order) => {
                const queueStateMeta = getQueueStateMeta(order);

                return {
                    ...order,
                    queueState: queueStateMeta.label,
                    queueTone: queueStateMeta.tone,
                };
            });
    }, [orders, quickSearch, categoryFilter, channelFilter]);

    const readyOrdersCount = useMemo(
        () => displayedOrders.filter((order) => order.queueState === 'Ready').length,
        [displayedOrders]
    );

    const activeIssueCount = useMemo(
        () => displayedOrders.filter((order) => order.queueState === 'Issue Active').length,
        [displayedOrders]
    );

    const dispatchedCount = useMemo(
        () => displayedOrders.filter((order) => order.queueState === 'Dispatched').length,
        [displayedOrders]
    );

    const cancelledCount = useMemo(
        () => displayedOrders.filter((order) => order.queueState === 'Cancelled').length,
        [displayedOrders]
    );

    const hasLocalFilters = Boolean(
        normalizeSearchValue(quickSearch) || categoryFilter !== 'ALL' || channelFilter !== 'ALL'
    );

    const scopeChips = [
        {
            key: 'loaded',
            label: hasLocalFilters ? `${orders.length} loaded` : `${displayedOrders.length} in scope`,
            color: 'default',
            variant: 'outlined',
        },
        client !== 'ALL' && {
            key: 'client',
            label: `Client: ${client}`,
            color: 'default',
            variant: 'outlined',
        },
        !useCutoff && status !== 'ALL' && {
            key: 'status',
            label: `Status: ${status}`,
            color: 'default',
            variant: 'outlined',
        },
        issueStatus !== 'ALL' && {
            key: 'issueStatus',
            label: `Issue: ${issueStatus}`,
            color: 'default',
            variant: 'outlined',
        },
        categoryFilter !== 'ALL' && {
            key: 'category',
            label: `Category: ${categoryFilter}`,
            color: 'default',
            variant: 'outlined',
        },
        channelFilter !== 'ALL' && {
            key: 'channel',
            label: `Channel: ${channelFilter}`,
            color: 'default',
            variant: 'outlined',
        },
        quickSearch && {
            key: 'quickSearch',
            label: `Search: ${quickSearch}`,
            color: 'default',
            variant: 'outlined',
        },
        useCutoff
            ? {
                key: 'dispatchToday',
                label: 'Required Dispatch Today',
                color: 'warning',
                variant: 'filled',
            }
            : {
                key: 'dateRange',
                label: `${dateFrom} to ${dateTo}`,
                color: 'default',
                variant: 'outlined',
            },
        {
            key: 'viewPreset',
            label: viewPreset === 'operations' ? 'Operations View' : 'Full View',
            color: viewPreset === 'operations' ? 'secondary' : 'info',
            variant: 'filled',
        },
    ].filter(Boolean);

    const queueSummaryChips = [
        {
            key: 'shown',
            label: `${displayedOrders.length} shown`,
            color: 'primary',
            variant: 'filled',
        },
        {
            key: 'ready',
            label: `Ready: ${readyOrdersCount}`,
            color: 'info',
            variant: readyOrdersCount > 0 ? 'filled' : 'outlined',
        },
        {
            key: 'issues',
            label: `Issues: ${activeIssueCount}`,
            color: 'warning',
            variant: activeIssueCount > 0 ? 'filled' : 'outlined',
        },
        {
            key: 'dispatched',
            label: `Dispatched: ${dispatchedCount}`,
            color: 'success',
            variant: 'outlined',
        },
        {
            key: 'cancelled',
            label: `Cancelled: ${cancelledCount}`,
            color: 'default',
            variant: 'outlined',
        },
    ];

    const getActionButtonSx = (paletteKey, variant = 'soft') => {
        const paletteColor = theme.palette[paletteKey]?.main || theme.palette.grey[700];
        const isFilled = variant === 'filled';
        const isGhost = variant === 'ghost';

        return {
            width: 32,
            height: 32,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: isGhost ? alpha(paletteColor, 0.14) : alpha(paletteColor, isFilled ? 0.18 : 0.2),
            color: isFilled ? theme.palette.common.white : paletteColor,
            backgroundColor: isFilled
                ? paletteColor
                : isGhost
                    ? alpha(paletteColor, 0.02)
                    : alpha(paletteColor, 0.05),
            '&:hover': {
                backgroundColor: isFilled
                    ? paletteColor
                    : isGhost
                        ? alpha(paletteColor, 0.07)
                        : alpha(paletteColor, 0.1),
                borderColor: alpha(paletteColor, isGhost ? 0.2 : 0.26),
            },
        };
    };

    const columns = [
        {
            field: 'actions',
            headerName: 'Act',
            width: 116,
            sortable: false,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const canDispatch = params.row.orderStatus === 'New';
                const canCancel =
                    params.row.clientName === 'Funky Pigeon' &&
                    params.row.category === 'GiftOnly' &&
                    params.row.orderStatus === 'New';
                const hasIssue = params.row.queueState === 'Issue Active';
                const issuePalette = hasIssue ? 'warning' : 'primary';

                return (
                    <Stack direction="row" spacing={0.5} sx={{ width: '100%', justifyContent: 'center' }}>
                        {canDispatch && (
                            <Tooltip title="Dispatch order">
                                <IconButton
                                    size="small"
                                    aria-label={`Dispatch order ${params.row.orderNumber}`}
                                    onClick={() => handleDispatchClick(params.row)}
                                    sx={getActionButtonSx('secondary', 'filled')}
                                >
                                    <LocalShipping fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Tooltip title={params.row.queueState === 'Issue Active' ? 'Update active issue' : 'Add issue'}>
                            <IconButton
                                size="small"
                                aria-label={`Add issue for order ${params.row.orderNumber}`}
                                onClick={() => handleAddIssueClick(params.row)}
                                sx={getActionButtonSx(issuePalette, hasIssue ? 'soft' : 'ghost')}
                            >
                                <ErrorOutline fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        {canCancel && (
                            <Tooltip title="Cancel order">
                                <IconButton
                                    size="small"
                                    aria-label={`Cancel order ${params.row.orderNumber}`}
                                    onClick={() => handleCancelClick(params.row)}
                                    sx={getActionButtonSx('error', 'ghost')}
                                >
                                    <CancelOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                );
            },
        },
        {
            field: 'orderNumber',
            headerName: 'Order Number',
            minWidth: 145,
            flex: 0.9,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', height: '100%' }}>
                    {params.value || 'N/A'}
                </Typography>
            ),
        },
        {
            field: 'queueState',
            headerName: 'Queue',
            minWidth: 132,
            flex: 0.9,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    color={params.row.queueTone}
                    variant={params.row.queueTone === 'default' ? 'outlined' : 'filled'}
                />
            ),
        },
        {
            field: 'clientName',
            headerName: 'Client',
            minWidth: 145,
            flex: 1,
        },
        {
            field: 'category',
            headerName: 'Category',
            minWidth: 118,
            flex: 0.8,
            renderCell: (params) => (
                params.value ? (
                    <Chip label={params.value} size="small" sx={getCategoryChipStyles(params.value)} />
                ) : (
                    <Typography variant="body2" color="text.secondary">N/A</Typography>
                )
            ),
        },
        {
            field: 'orderStatus',
            headerName: 'Status',
            minWidth: 108,
            flex: 0.8,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    sx={{
                        ...getOrderStatusChipStyles(params.value),
                    }}
                />
            ),
        },
        {
            field: 'issueStatus',
            headerName: 'Issue',
            minWidth: 118,
            flex: 0.8,
            renderCell: (params) =>
                params.value ? (
                    <Chip
                        label={params.value}
                        size="small"
                        sx={{
                            ...getChipStyles(params.value),
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        N/A
                    </Typography>
                ),
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            minWidth: 145,
            flex: 0.95,
            valueFormatter: (value) => formatDateTime(value),
        },
        {
            field: 'dispatchedDate',
            headerName: 'Dispatched',
            minWidth: 145,
            flex: 0.95,
            valueFormatter: (value) => formatDateTime(value),
        },
        {
            field: 'dispatchedByUser',
            headerName: 'Dispatched By',
            minWidth: 135,
            flex: 0.9,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: params.value ? 600 : 400,
                        color: params.value ? 'text.primary' : 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    {params.value || 'N/A'}
                </Typography>
            ),
        },
        {
            field: 'fulfilmentChannel',
            headerName: 'Channel',
            minWidth: 105,
            flex: 0.8,
        },
        {
            field: 'shippingCarrier',
            headerName: 'Carrier',
            minWidth: 118,
            flex: 0.9,
        },
        {
            field: 'shippingMethod',
            headerName: 'Method',
            minWidth: 150,
            flex: 1.2,
        },
        {
            field: 'tracking',
            headerName: 'Tracking',
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: params.value ? 'monospace' : 'inherit',
                        color: params.value ? 'text.primary' : 'text.secondary',
                    }}
                >
                    {params.value || 'N/A'}
                </Typography>
            ),
        },
    ];

    return (
        <Layout>
            <Box sx={{ pt: { xs: 1, md: 1.25 }, px: { xs: 2, md: 3 }, pb: { xs: 2, md: 2.5 } }}>
                <Box
                    sx={{
                        mb: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <Box>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'text.primary', mb: 0.125, lineHeight: 1.2 }}>
                            Client's Orders
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Compact dispatch work queue focused on fast table scanning and actioning.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip
                            label={viewPreset === 'operations' ? 'Operations View' : 'Full View'}
                            color={viewPreset === 'operations' ? 'secondary' : 'info'}
                            variant="filled"
                            size="small"
                        />
                        <Chip
                            label={useCutoff ? 'Required Dispatch Today' : `${dateFrom} to ${dateTo}`}
                            color={useCutoff ? 'warning' : 'default'}
                            variant={useCutoff ? 'filled' : 'outlined'}
                            size="small"
                        />
                    </Stack>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 1.25, md: 1.5 },
                        mb: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2.5,
                        background: 'linear-gradient(180deg, #fbfdff 0%, #ffffff 100%)',
                        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.04)',
                    }}
                >
                    <Stack spacing={1.25}>
                        <Stack
                            direction={{ xs: 'column', xl: 'row' }}
                            spacing={1}
                            justifyContent="space-between"
                            alignItems={{ xl: 'center' }}
                        >
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.25 }}>
                                    Filter and Triage
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Set the server scope first, then narrow the loaded queue instantly with quick triage filters.
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={useCutoff}
                                    onClick={() => handleApplyDatePreset(0)}
                                >
                                    Today
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={useCutoff}
                                    onClick={() => handleApplyDatePreset(2)}
                                >
                                    Last 3 Days
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={useCutoff}
                                    onClick={() => handleApplyDatePreset(6)}
                                >
                                    Last 7 Days
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<SearchRounded />}
                                    onClick={handleSearch}
                                >
                                    Apply Filters
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<RestartAltRounded />}
                                    onClick={handleResetFilters}
                                >
                                    Reset
                                </Button>
                            </Stack>
                        </Stack>

                        <Box
                            sx={{
                                p: 1.25,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.common.white, 0.78),
                            }}
                        >
                            <Stack spacing={1}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: { xs: 'flex-start', md: 'center' },
                                        gap: 1,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Box>
                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                                            Filters
                                        </Typography>
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            One compact bar for server scope and quick triage.
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={hasLocalFilters ? 'Local filters active' : 'Scope only'}
                                        size="small"
                                        color={hasLocalFilters ? 'primary' : 'default'}
                                        variant={hasLocalFilters ? 'filled' : 'outlined'}
                                    />
                                </Box>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={12} sm={6} md={2}>
                                        <TextField
                                            id="client"
                                            label="Client"
                                            value={client}
                                            onChange={(e) => setClient(e.target.value)}
                                            fullWidth
                                            size="small"
                                            select
                                        >
                                            <MenuItem value="ALL">ALL</MenuItem>
                                            {clients.map((clientItem) => (
                                                <MenuItem key={clientItem.clientName} value={clientItem.clientName}>
                                                    {clientItem.clientName}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={1.4}>
                                        <TextField
                                            id="status"
                                            label="Status"
                                            value={status}
                                            onChange={(e) => setStatus(e.target.value)}
                                            fullWidth
                                            size="small"
                                            select
                                            disabled={useCutoff}
                                        >
                                            <MenuItem value="ALL">ALL</MenuItem>
                                            <MenuItem value="New">New</MenuItem>
                                            <MenuItem value="Dispatched">Dispatched</MenuItem>
                                            <MenuItem value="Cancelled">Cancelled</MenuItem>
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={1.4}>
                                        <TextField
                                            id="issue-status"
                                            label="Issue"
                                            value={issueStatus}
                                            onChange={(e) => setIssueStatus(e.target.value)}
                                            fullWidth
                                            size="small"
                                            select
                                            disabled={useCutoff}
                                        >
                                            <MenuItem value="ALL">ALL</MenuItem>
                                            {issueStatuses.map((statusItem) => (
                                                <MenuItem key={statusItem.statusId} value={statusItem.statusName}>
                                                    {statusItem.statusName}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={1.5}>
                                        <TextField
                                            id="from-date"
                                            label="From Date"
                                            type="date"
                                            value={dateFrom}
                                            onChange={handleDateFromChange}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            disabled={useCutoff}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={1.5}>
                                        <TextField
                                            id="to-date"
                                            label="To Date"
                                            type="date"
                                            value={dateTo}
                                            onChange={handleDateToChange}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            disabled={useCutoff}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={2.4}>
                                        <TextField
                                            label="Quick Search"
                                            placeholder="Order, client, carrier, tracking..."
                                            value={quickSearch}
                                            onChange={(e) => setQuickSearch(e.target.value)}
                                            fullWidth
                                            size="small"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchRounded fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={1.4}>
                                        <TextField
                                            label="Category"
                                            value={categoryFilter}
                                            onChange={(e) => setCategoryFilter(e.target.value)}
                                            fullWidth
                                            size="small"
                                            select
                                        >
                                            <MenuItem value="ALL">ALL</MenuItem>
                                            {availableCategories.map((category) => (
                                                <MenuItem key={category} value={category}>
                                                    {category}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={1.4}>
                                        <TextField
                                            label="Channel"
                                            value={channelFilter}
                                            onChange={(e) => setChannelFilter(e.target.value)}
                                            fullWidth
                                            size="small"
                                            select
                                        >
                                            <MenuItem value="ALL">ALL</MenuItem>
                                            {availableChannels.map((channel) => (
                                                <MenuItem key={channel} value={channel}>
                                                    {channel}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={1.6}>
                                        <ToggleButtonGroup
                                            value={viewPreset}
                                            exclusive
                                            onChange={(e, newValue) => {
                                                if (newValue) {
                                                    setViewPreset(newValue);
                                                }
                                            }}
                                            size="small"
                                            fullWidth
                                            sx={{ height: '100%' }}
                                        >
                                            <ToggleButton value="operations" sx={{ flex: 1, gap: 0.5 }}>
                                                <ViewCompactRounded fontSize="small" />
                                                Ops
                                            </ToggleButton>
                                            <ToggleButton value="full" sx={{ flex: 1, gap: 0.5 }}>
                                                <TableRowsRounded fontSize="small" />
                                                Full
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Grid>

                                    <Grid item xs={12} md={2.2} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={useCutoff}
                                                    onChange={handleCutoffChange}
                                                />
                                            }
                                            label="Required Dispatch Today"
                                            sx={{ mr: 0 }}
                                        />
                                    </Grid>
                                </Grid>
                            </Stack>
                        </Box>
                    </Stack>
                </Paper>

                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                    {scopeChips.map((chip) => (
                        <Chip
                            key={chip.key}
                            label={chip.label}
                            color={chip.color}
                            variant={chip.variant}
                            size="small"
                        />
                    ))}
                    {queueSummaryChips.map((chip) => (
                        <Chip
                            key={chip.key}
                            label={chip.label}
                            color={chip.color}
                            variant={chip.variant}
                            size="small"
                        />
                    ))}
                </Stack>

                <Paper
                    elevation={0}
                    sx={{
                        p: 1.25,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2.5,
                        boxShadow: '0 10px 22px rgba(15, 23, 42, 0.05)',
                    }}
                >
                    <Stack spacing={1}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={0.75}
                            justifyContent="space-between"
                            alignItems={{ md: 'center' }}
                        >
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    Orders Work Queue
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Inline actions appear when an order is ready to dispatch, needs an issue update, or can be cancelled.
                                </Typography>
                            </Box>
                            <Chip
                                label={readyOrdersCount > 0 ? `${readyOrdersCount} ready now` : 'No ready orders in view'}
                                color={readyOrdersCount > 0 ? 'info' : 'default'}
                                variant={readyOrdersCount > 0 ? 'filled' : 'outlined'}
                                size="small"
                            />
                        </Stack>

                        <Divider />

                        <Box sx={{ height: { xs: 560, md: 'calc(100vh - 270px)' }, minHeight: 560, width: '100%' }}>
                            <DataGrid
                                className="whiteTable"
                                rows={displayedOrders}
                                columns={columns}
                                slots={{ toolbar: OrdersGridToolbar }}
                                getRowId={(row) => row.orderId}
                                columnVisibilityModel={columnVisibilityModel}
                                onColumnVisibilityModelChange={setColumnVisibilityModel}
                                disableRowSelectionOnClick
                                rowHeight={48}
                                columnHeaderHeight={42}
                                localeText={{
                                    noRowsLabel: hasLocalFilters
                                        ? 'No orders match the quick triage filters. Clear search, category, or channel to see the loaded queue.'
                                        : 'No orders found in this server scope. Adjust client, dates, status, or Required Dispatch Today.',
                                }}
                                pageSizeOptions={[25, 50, 100]}
                                initialState={{
                                    pagination: {
                                        paginationModel: { pageSize: 25, page: 0 },
                                    },
                                    sorting: {
                                        sortModel: [{ field: 'createdAt', sort: 'desc' }],
                                    },
                                }}
                                getRowClassName={(params) => (
                                    [
                                        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd',
                                        params.row.queueState === 'Issue Active' && 'row-attention',
                                        params.row.queueState === 'Ready' && 'row-ready',
                                        params.row.queueState === 'Dispatched' && 'row-complete',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')
                                )}
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-row': {
                                        maxHeight: 'none !important',
                                    },
                                    '& .MuiDataGrid-toolbarContainer button': {
                                        textTransform: 'none',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        color: '#555',
                                        borderColor: 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-cellContent': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        height: '100%',
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#f7f9fc',
                                        color: '#333',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        fontWeight: 700,
                                    },
                                    '& .MuiDataGrid-virtualScroller': {
                                        backgroundColor: '#fff',
                                    },
                                    '& .MuiDataGrid-row.odd': {
                                        backgroundColor: '#fcfcfd',
                                    },
                                    '& .MuiDataGrid-row.row-attention': {
                                        backgroundColor: alpha(theme.palette.warning.main, 0.07),
                                    },
                                    '& .MuiDataGrid-row.row-ready': {
                                        backgroundColor: alpha(theme.palette.info.main, 0.05),
                                    },
                                    '& .MuiDataGrid-row.row-complete': {
                                        backgroundColor: alpha(theme.palette.success.main, 0.045),
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        backgroundColor: '#f3f7ff',
                                    },
                                    '& .MuiDataGrid-footerContainer': {
                                        backgroundColor: '#f7f9fc',
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                    },
                                }}
                            />
                        </Box>
                    </Stack>
                </Paper>

                {/* Issue Dialog */}
                {selectedOrder && (
                    <IssueDialog
                        open={openIssueDialog}
                        onClose={handleIssueDialogClose}
                        order={selectedOrder}
                        onIssueAdded={handleIssueAdded}
                    />
                )}

                {/* Dispatch Dialog */}
                {dispatchOrder && (
                    <DispatchDialog
                        open={openDispatchDialog}
                        onClose={handleDispatchDialogClose}
                        order={dispatchOrder}
                        onDispatched={handleOrderDispatched}
                    />
                )}

                {/* Cancel Order Dialog */}
                {cancelOrderObj && (
                    <CancelOrderDialog
                        open={openCancelDialog}
                        onClose={handleCancelDialogClose}
                        order={cancelOrderObj}
                        onCancelled={handleOrderCancelled}
                        onCancelError={handleCancelError}
                    />
                )}
            </Box>
        </Layout>
    );

};

export default Orders;
